import { forwardRef } from 'react';
import { Button as AntButton } from 'antd';
import type { ButtonProps as AntButtonProps } from 'antd';

export interface ButtonProps extends Omit<AntButtonProps, 'variant' | 'size' | 'type'> {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'danger';
  size?: 'sm' | 'md' | 'lg' | AntButtonProps['size'];
  /** Accept native types during migration; prefer htmlType for form actions. */
  type?: AntButtonProps['type'] | 'button' | 'submit' | 'reset';
}

/** Dashboard action button. Geometry and states live in the shared Ant Design theme. */
const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(function Button(
  { variant, size = 'md', type, htmlType, loading = false, disabled, danger, className = '', ...props },
  ref
) {
  const nativeType = type === 'button' || type === 'submit' || type === 'reset' ? type : undefined;
  const antType = nativeType ? undefined : (type as AntButtonProps['type']);
  const roleType = variant === 'secondary' ? 'default' : variant === 'tertiary' ? 'text' : 'primary';
  const antSize = size === 'sm' ? 'small' : size === 'lg' ? 'large' : size === 'md' ? 'middle' : size;

  return (
    <AntButton
      {...props}
      ref={ref}
      type={variant ? roleType : antType ?? 'primary'}
      htmlType={htmlType ?? nativeType ?? 'button'}
      size={antSize}
      danger={danger || variant === 'danger'}
      loading={loading}
      disabled={disabled || Boolean(loading)}
      aria-busy={props['aria-busy'] ?? (loading ? true : undefined)}
      className={`dashboard-button ${className}`.trim()} />
  );
});

export default Button;
